<template>
    <div class="automation-permission-settings">
        <div class="mode-switch-section">
            <div class="mode-label">
                <i class="material-icons">{{ permissionMode === 'public' ? 'public' : 'lock' }}</i>
                <span class="mode-title">{{ $t('automation.permissions.mode') }}</span>
            </div>
            <div class="mode-switch">
                <a-switch 
                    v-model="isCustomMode" 
                    @change="onModeChange"
                    :disabled="saving"
                >
                    <span slot="checkedChildren">{{ $t('automation.permissions.customMode') }}</span>
                    <span slot="unCheckedChildren">{{ $t('automation.permissions.publicMode') }}</span>
                </a-switch>
            </div>
            <div class="mode-description">
                <span v-if="permissionMode === 'public'">
                    {{ $t('automation.permissions.publicModeDesc') }}
                </span>
                <span v-else>
                    {{ $t('automation.permissions.customModeDesc') }}
                </span>
            </div>
        </div>

        <div v-if="permissionMode === 'custom'" class="custom-permissions">
            <a-tabs v-model="activeTab" type="card">
                <a-tab-pane key="users" :tab="$t('automation.permissions.usersTab')">
                    <AutomationUserPermissionsTab
                        :users="users"
                        :departments="departments"
                        :permission-targets="permissionTargets"
                        :scope-groups="scopeGroups"
                        :current-user-id="currentUserId"
                        @toggle-scope="onToggleScope"
                        @activate-target="onActivateTarget"
                    />
                </a-tab-pane>
                <a-tab-pane key="departments" :tab="$t('automation.permissions.departmentsTab')">
                    <AutomationDepartmentPermissionsTab
                        :departments="departments"
                        :users="users"
                        :permission-targets="permissionTargets"
                        :scope-groups="scopeGroups"
                        @toggle-scope="onToggleScope"
                        @activate-target="onActivateTarget"
                    />
                </a-tab-pane>
            </a-tabs>
        </div>

        <div class="settings-footer">
            <SupsisButton 
                ghost 
                @click="openApplyToOthersModal" 
                :disabled="saving"
                v-tippy="{ content: $t('automation.permissions.applyToOthersDesc') }"
            >
                <i class="material-icons">content_copy</i>
                {{ $t('automation.permissions.applyToOthers') }}
            </SupsisButton>
            <div class="footer-right">
                <SupsisButton @click="onCancel" :disabled="saving">
                    {{ $t('common.cancel') }}
                </SupsisButton>
                <SupsisButton primary @click="onSave" :loading="saving">
                    {{ $t('common.save') }}
                </SupsisButton>
            </div>
        </div>

        <!-- Apply to Others Modal -->
        <a-modal
            :title="$t('automation.permissions.applyToOthersTitle')"
            v-model="applyToOthersModalVisible"
            :width="700"
            :footer="null"
            :maskClosable="false"
        >
            <div class="apply-to-others-modal">
                <div class="modal-description">
                    <a-alert 
                        :message="$t('automation.permissions.applyToOthersWarning')" 
                        type="warning" 
                        show-icon 
                    />
                </div>

                <div class="search-section">
                    <a-input-search
                        v-model="applySearchText"
                        :placeholder="$t('automation.permissions.searchPlaceholder')"
                        class="search-input"
                    />
                </div>

                <div class="automation-list-section">
                    <div class="list-header">
                        <a-checkbox 
                            :checked="isAllSelected" 
                            :indeterminate="isIndeterminate"
                            @change="toggleSelectAll"
                        >
                            {{ $t('automation.permissions.selectAll') }} ({{ filteredOtherAutomations.length }})
                        </a-checkbox>
                    </div>
                    <div class="automation-list">
                        <template v-for="(category, categoryName) in categorizedOtherAutomations">
                            <div 
                                :key="'cat-' + categoryName" 
                                class="category-header"
                                @click="toggleCategorySelection(categoryName)"
                            >
                                <a-checkbox 
                                    :checked="isCategorySelected(categoryName)" 
                                    :indeterminate="isCategoryIndeterminate(categoryName)"
                                    @click.stop
                                    @change="toggleCategorySelection(categoryName)"
                                />
                                <i class="material-icons">folder</i>
                                <span class="category-name">{{ categoryName }}</span>
                                <span class="category-count">({{ category.length }})</span>
                            </div>
                            <div 
                                v-for="auto in category" 
                                :key="auto._id"
                                class="automation-item"
                                :class="{ selected: selectedAutomationIds.includes(auto._id) }"
                                @click="toggleAutomationSelection(auto._id)"
                            >
                                <a-checkbox :checked="selectedAutomationIds.includes(auto._id)" />
                                <div class="automation-info">
                                    <span class="automation-name">{{ auto.name }}</span>
                                </div>
                                <div class="automation-status">
                                    <a-tag v-if="auto.published" color="green" size="small">{{ $t('automation.published') }}</a-tag>
                                    <a-tag v-else color="default" size="small">{{ $t('auto.automationUnpublishedTag') }}</a-tag>
                                </div>
                            </div>
                        </template>
                        <div v-if="filteredOtherAutomations.length === 0" class="empty-list">
                            <i class="material-icons">search_off</i>
                            <span>{{ $t('automation.permissions.noAutomationsFound') }}</span>
                        </div>
                    </div>
                </div>

                <div class="modal-footer">
                    <span class="selected-count">
                        {{ $t('automation.permissions.selectedCount', { count: selectedAutomationIds.length }) }}
                    </span>
                    <div class="footer-buttons">
                        <SupsisButton @click="closeApplyToOthersModal">
                            {{ $t('common.cancel') }}
                        </SupsisButton>
                        <SupsisButton 
                            primary 
                            @click="applyToSelectedAutomations" 
                            :loading="applyingToOthers"
                            :disabled="selectedAutomationIds.length === 0"
                        >
                            {{ $t('automation.permissions.applyToSelected') }}
                        </SupsisButton>
                    </div>
                </div>
            </div>
        </a-modal>
    </div>
</template>

<script>
import Vue from "vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import AutomationUserPermissionsTab from "./permissions/AutomationUserPermissionsTab.vue";
import AutomationDepartmentPermissionsTab from "./permissions/AutomationDepartmentPermissionsTab.vue";
import { 
    AUTOMATION_SCOPE_GROUPS,
    AUTOMATION_PERMISSION_SCOPES 
} from "../../../../utils/AutomationPermissionScopes";
import { 
    getAutomationScopeDependencies,
    findDependentAutomationScopes 
} from "../../../../utils/AutomationPermissionUtils";
import { AUTOMATION_API } from "../../../../api/automation";
import { mapState } from "vuex";

export default Vue.extend({
    name: "AutomationPermissionSettings",
    components: {
        SupsisButton,
        AutomationUserPermissionsTab,
        AutomationDepartmentPermissionsTab,
    },
    props: {
        automation: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            permissionMode: "public",
            permissionTargets: [],
            saving: false,
            activeTab: "users",
            applyToOthersModalVisible: false,
            applySearchText: "",
            selectedAutomationIds: [],
            applyingToOthers: false,
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data || [],
            departments: (state) => state.subject.subjects.data || [],
            currentUser: (state) => state.auth.user,
            allAutomations: (state) => state.automation.automations || [],
        }),
        currentUserId() {
            return this.currentUser ? this.currentUser._id : null;
        },
        isCustomMode: {
            get() {
                return this.permissionMode === "custom";
            },
            set(value) {
                this.permissionMode = value ? "custom" : "public";
            },
        },
        scopeGroups() {
            return Object.values(AUTOMATION_SCOPE_GROUPS);
        },
        otherAutomations() {
            var self = this;
            return this.allAutomations.filter(function(auto) {
                return auto._id !== self.automation._id;
            });
        },
        filteredOtherAutomations() {
            var self = this;
            if (!this.applySearchText) {
                return this.otherAutomations;
            }
            var searchLower = this.applySearchText.toLowerCase();
            return this.otherAutomations.filter(function(auto) {
                var nameMatch = auto.name.toLowerCase().includes(searchLower);
                var categoryName = auto.category && auto.category.name ? auto.category.name : "Genel";
                var categoryMatch = categoryName.toLowerCase().includes(searchLower);
                return nameMatch || categoryMatch;
            });
        },
        categorizedOtherAutomations() {
            var categories = {};
            this.filteredOtherAutomations.forEach(function(auto) {
                var categoryName = auto.category && auto.category.name ? auto.category.name : "Genel";
                if (!categories[categoryName]) {
                    categories[categoryName] = [];
                }
                categories[categoryName].push(auto);
            });
            return categories;
        },
        categoryNames() {
            return Object.keys(this.categorizedOtherAutomations);
        },
        isAllSelected() {
            return this.filteredOtherAutomations.length > 0 && 
                this.filteredOtherAutomations.every(function(auto) {
                    return this.selectedAutomationIds.includes(auto._id);
                }, this);
        },
        isIndeterminate() {
            var selectedInFiltered = this.filteredOtherAutomations.filter(function(auto) {
                return this.selectedAutomationIds.includes(auto._id);
            }, this);
            return selectedInFiltered.length > 0 && selectedInFiltered.length < this.filteredOtherAutomations.length;
        },
    },
    methods: {
        loadPermissionConfig() {
            var config = this.automation.permissionConfig || {};
            this.permissionMode = config.mode || "public";
            this.permissionTargets = config.custom && Array.isArray(config.custom) ? JSON.parse(JSON.stringify(config.custom)) : [];
        },
        async fetchPermissionConfig() {
            try {
                var response = await AUTOMATION_API.getPermissionConfig(this.automation._id);
                var config = response.permissionConfig || {};
                this.permissionMode = config.mode || "public";
                this.permissionTargets = config.custom && Array.isArray(config.custom) ? JSON.parse(JSON.stringify(config.custom)) : [];
            } catch (error) {
                console.error("Error fetching permission config:", error);
                // Fallback to prop data
                this.loadPermissionConfig();
            }
        },
        onModeChange(isCustom) {
            var self = this;
            if (!isCustom) {
                this.$confirm({
                    title: this.$t("automation.permissions.confirmPublicMode"),
                    content: this.$t("automation.permissions.confirmPublicModeDesc"),
                    okText: this.$t("common.yes"),
                    cancelText: this.$t("common.no"),
                    onOk: function() {
                        self.permissionMode = "public";
                        self.permissionTargets = [];
                    },
                    onCancel: function() {
                        self.isCustomMode = true;
                    },
                });
            } else {
                this.addDefaultUsers();
            }
        },
        addDefaultUsers() {
            var fullScopes = Object.values(AUTOMATION_PERMISSION_SCOPES);
            var usersToAdd = [];
            var self = this;

            // 1. Şu anki kullanıcı
            if (this.currentUser) {
                usersToAdd.push(this.currentUser._id);
            }

            // 2. Root kullanıcılar
            var rootUsers = this.users.filter(function(u) {
                return u.role && u.role.isRoot;
            });
            rootUsers.forEach(function(u) {
                if (!usersToAdd.includes(u._id)) {
                    usersToAdd.push(u._id);
                }
            });

            // 3. Owner kullanıcı
            var ownerUser = this.users.find(function(u) {
                return u.isOwner;
            });
            if (ownerUser && !usersToAdd.includes(ownerUser._id)) {
                usersToAdd.push(ownerUser._id);
            }

            // 4. Otomasyonu oluşturan
            if (this.automation && this.automation.createdBy) {
                var creatorId = typeof this.automation.createdBy === "object" 
                    ? this.automation.createdBy._id 
                    : this.automation.createdBy;
                
                if (!usersToAdd.includes(creatorId)) {
                    usersToAdd.push(creatorId);
                }
            }

            var newTargets = usersToAdd.map(function(userId) {
                return {
                    target: "user",
                    user: userId,
                    department: null,
                    scopes: fullScopes,
                };
            });

            this.permissionTargets = newTargets.slice();
        },
        onActivateTarget(payload) {
            var targetType = payload.targetType;
            var targetId = payload.targetId;
            var self = this;
            
            var existingIndex = this.permissionTargets.findIndex(function(t) {
                if (targetType === "user") {
                    return t.target === "user" && t.user && t.user.toString() === targetId.toString();
                } else {
                    return t.target === "department" && t.department && t.department.toString() === targetId.toString();
                }
            });

            if (existingIndex === -1) {
                var fullScopes = Object.values(AUTOMATION_PERMISSION_SCOPES);
                var newTarget = {
                    target: targetType,
                    user: targetType === "user" ? targetId : null,
                    department: targetType === "department" ? targetId : null,
                    scopes: fullScopes,
                };
                this.permissionTargets = this.permissionTargets.concat([newTarget]);
            } else {
                this.permissionTargets = this.permissionTargets.filter(function(t, i) {
                    return i !== existingIndex;
                });
            }
        },
        onToggleScope(payload) {
            var targetIndex = payload.targetIndex;
            var scope = payload.scope;
            var target = this.permissionTargets[targetIndex];
            if (!target) {
                return;
            }

            var currentScopes = target.scopes || [];
            
            if (currentScopes.includes(scope)) {
                var dependentScopes = findDependentAutomationScopes(scope);
                var newScopes = currentScopes.filter(function(s) {
                    return s !== scope && !dependentScopes.includes(s);
                });
                this.$set(this.permissionTargets[targetIndex], "scopes", newScopes);
            } else {
                var dependencies = getAutomationScopeDependencies(scope);
                var scopesToAdd = [scope].concat(dependencies);
                var uniqueScopes = currentScopes.concat(scopesToAdd).filter(function(s, i, arr) {
                    return arr.indexOf(s) === i;
                });
                this.$set(this.permissionTargets[targetIndex], "scopes", uniqueScopes);
            }
            
            this.permissionTargets = this.permissionTargets.slice();
        },
        async onSave() {
            this.saving = true;
            try {
                var permissionConfig = {
                    mode: this.permissionMode,
                    custom: this.permissionMode === "custom" ? this.permissionTargets : [],
                };

                await AUTOMATION_API.updatePermissionConfig(this.automation._id, permissionConfig);
                
                this.$message.success(this.$t("automation.permissions.saveSuccess"));
                this.$emit("saved", permissionConfig);
                this.$emit("close");
            } catch (error) {
                console.error("Error saving permission config:", error);
                this.$message.error(this.$t("automation.permissions.saveError"));
            } finally {
                this.saving = false;
            }
        },
        onCancel() {
            this.$emit("close");
        },
        openApplyToOthersModal() {
            this.applyToOthersModalVisible = true;
            this.applySearchText = "";
            this.selectedAutomationIds = [];
        },
        closeApplyToOthersModal() {
            this.applyToOthersModalVisible = false;
            this.applySearchText = "";
            this.selectedAutomationIds = [];
        },
        toggleAutomationSelection(automationId) {
            var index = this.selectedAutomationIds.indexOf(automationId);
            if (index === -1) {
                this.selectedAutomationIds.push(automationId);
            } else {
                this.selectedAutomationIds.splice(index, 1);
            }
        },
        toggleSelectAll() {
            var self = this;
            if (this.isAllSelected) {
                // Deselect all filtered
                this.selectedAutomationIds = this.selectedAutomationIds.filter(function(id) {
                    return !self.filteredOtherAutomations.some(function(auto) {
                        return auto._id === id;
                    });
                });
            } else {
                // Select all filtered
                this.filteredOtherAutomations.forEach(function(auto) {
                    if (!self.selectedAutomationIds.includes(auto._id)) {
                        self.selectedAutomationIds.push(auto._id);
                    }
                });
            }
        },
        isCategorySelected(categoryName) {
            var self = this;
            var categoryAutomations = this.categorizedOtherAutomations[categoryName] || [];
            if (categoryAutomations.length === 0) return false;
            return categoryAutomations.every(function(auto) {
                return self.selectedAutomationIds.includes(auto._id);
            });
        },
        isCategoryIndeterminate(categoryName) {
            var self = this;
            var categoryAutomations = this.categorizedOtherAutomations[categoryName] || [];
            if (categoryAutomations.length === 0) return false;
            var selectedInCategory = categoryAutomations.filter(function(auto) {
                return self.selectedAutomationIds.includes(auto._id);
            });
            return selectedInCategory.length > 0 && selectedInCategory.length < categoryAutomations.length;
        },
        toggleCategorySelection(categoryName) {
            var self = this;
            var categoryAutomations = this.categorizedOtherAutomations[categoryName] || [];
            if (this.isCategorySelected(categoryName)) {
                // Deselect all in category
                this.selectedAutomationIds = this.selectedAutomationIds.filter(function(id) {
                    return !categoryAutomations.some(function(auto) {
                        return auto._id === id;
                    });
                });
            } else {
                // Select all in category
                categoryAutomations.forEach(function(auto) {
                    if (!self.selectedAutomationIds.includes(auto._id)) {
                        self.selectedAutomationIds.push(auto._id);
                    }
                });
            }
        },
        async applyToSelectedAutomations() {
            var self = this;
            if (this.selectedAutomationIds.length === 0) {
                return;
            }

            this.applyingToOthers = true;
            var successCount = 0;
            var failCount = 0;

            var permissionConfig = {
                mode: this.permissionMode,
                custom: this.permissionMode === "custom" ? this.permissionTargets : [],
            };

            try {
                for (var i = 0; i < this.selectedAutomationIds.length; i++) {
                    var automationId = this.selectedAutomationIds[i];
                    try {
                        await AUTOMATION_API.updatePermissionConfig(automationId, permissionConfig);
                        successCount++;
                    } catch (error) {
                        console.error("Error applying permission to automation " + automationId, error);
                        failCount++;
                    }
                }

                if (successCount > 0) {
                    this.$message.success(
                        this.$t("automation.permissions.applySuccess", { count: successCount })
                    );
                }
                if (failCount > 0) {
                    this.$message.warning(
                        this.$t("automation.permissions.applyFailed", { count: failCount })
                    );
                }

                this.closeApplyToOthersModal();
                this.$store.dispatch("automation/syncAutomations");
            } catch (error) {
                console.error("Error applying permissions:", error);
                this.$message.error(this.$t("automation.permissions.applyError"));
            } finally {
                this.applyingToOthers = false;
            }
        },
    },
    async mounted() {
        if (!this.users || this.users.length === 0) {
            await this.$store.dispatch("user/getUsers", { perPage: 1000, page: 1 });
        }
        
        if (!this.departments || this.departments.length === 0) {
            await this.$store.dispatch("subject/getSubjects", { perPage: 1000, page: 1 });
        }
        
        // Her zaman API'den güncel veriyi çek
        await this.fetchPermissionConfig();
    },
});
</script>

<style scoped lang="scss">
.automation-permission-settings {
    display: flex;
    flex-direction: column;
    gap: 20px;

    .mode-switch-section {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 15px;
        background: var(--color-bg-neutral-default);
        border-radius: 8px;
        border: 1px solid var(--color-border-default);

        .mode-label {
            display: flex;
            align-items: center;
            gap: 8px;
            font-weight: 600;
            font-size: 16px;

            .material-icons {
                font-size: 20px;
            }
        }

        .mode-switch {
            display: flex;
            align-items: center;
        }

        .mode-description {
            font-size: 14px;
            color: var(--color-text-subdued);
        }
    }

    .custom-permissions {
        background: white;
        border-radius: 8px;
        border: 1px solid var(--color-border-default);
        overflow: hidden;
    }

    .settings-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
        padding-top: 15px;
        border-top: 1px solid var(--color-border-default);

        .footer-right {
            display: flex;
            gap: 10px;
        }
    }
}

.apply-to-others-modal {
    .modal-description {
        margin-bottom: 15px;
    }

    .search-section {
        margin-bottom: 15px;

        .search-input {
            width: 100%;
        }
    }

    .automation-list-section {
        border: 1px solid var(--color-border-default);
        border-radius: 8px;
        overflow: hidden;

        .list-header {
            padding: 12px 15px;
            background: var(--color-bg-neutral-default);
            border-bottom: 1px solid var(--color-border-default);
            font-weight: 500;
        }

        .automation-list {
            max-height: 350px;
            overflow-y: auto;

            .category-header {
                display: flex;
                align-items: center;
                gap: 10px;
                padding: 10px 15px;
                background: var(--color-bg-neutral-default);
                border-bottom: 1px solid var(--color-border-default);
                cursor: pointer;
                font-weight: 600;
                position: sticky;
                top: 0;
                z-index: 1;

                &:hover {
                    background: var(--color-bg-neutral-hover);
                }

                .material-icons {
                    font-size: 18px;
                    color: var(--color-icon-default);
                }

                .category-name {
                    flex: 1;
                }

                .category-count {
                    color: var(--color-text-subdued);
                    font-weight: 400;
                    font-size: 13px;
                }
            }

            .automation-item {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 10px 15px 10px 35px;
                border-bottom: 1px solid var(--color-border-subdued);
                cursor: pointer;
                transition: background-color 0.2s;

                &:last-child {
                    border-bottom: none;
                }

                &:hover {
                    background: var(--color-bg-neutral-hover);
                }

                &.selected {
                    background: var(--color-bg-primary-subdued);
                }

                .automation-info {
                    flex: 1;
                    min-width: 0;

                    .automation-name {
                        display: block;
                        font-weight: 500;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                }

                .automation-status {
                    flex-shrink: 0;
                }
            }

            .empty-list {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 40px;
                color: var(--color-text-subdued);

                .material-icons {
                    font-size: 48px;
                    margin-bottom: 10px;
                    opacity: 0.5;
                }
            }
        }
    }

    .modal-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 20px;
        padding-top: 15px;
        border-top: 1px solid var(--color-border-default);

        .selected-count {
            color: var(--color-text-subdued);
            font-size: 14px;
        }

        .footer-buttons {
            display: flex;
            gap: 10px;
        }
    }
}
</style>

<style lang="scss">
.automation-permission-settings {
    .ant-tabs-card {
        .ant-tabs-nav {
            margin: 0 !important;
        }

        .ant-tabs-content {
            padding: 0;
        }
    }
}
</style>
